import { useMutation, useQuery } from "@tanstack/react-query";
import { api, getClearApi } from "api";
import { T_Populate } from "types/_utils.types";
import { ApplicationSchemaDto } from "types/application.types";
import { BankSchemaDto } from "types/bank.types";
import { FileSchemaDto } from "types/file.types";
import { LawSuitSchemaDto, LawSuitTypes } from "types/lawsuit.types";
import { LoanSchemaDto } from "types/loan.types";
import { T_Api_Success_Res } from "./api.types";
import { APPLICATION_STATUS } from "types/application.types";
import { useAppSelector } from "store";
import { IntelligenceDto } from "types/intelligence.types";
import axios from "axios";
import { CreditorMonthlyWithhold } from "components/APRCharts/APRCharts";

enum BankFetchMethod {
  MONEYTHUMB = "MoneyThumb",
  PLAID = "Plaid",
}

export const onboardingService = {
  useGetAllApplication: () => {
    // const orgMeta = useAppSelector((state) => state.orgMeta);
    return useQuery({
      queryKey: ["application/all"],
      queryFn: async () => {
        const params = undefined;
        // if (orgMeta?.code) {
        //   params = { org_code: orgMeta?.code };
        // }
        const res = await getClearApi.get(`/application/all`, { params });
        return res.data as T_Api_Success_Res<ApplicationSchemaDto[]>;
      },
    });
  },

  useGetApplicationById: (applicationId: string | null) => {
    return useQuery({
      queryKey: ["application/single", applicationId],
      queryFn: async () => {
        const res = await getClearApi.get(`/application/${applicationId}`);
        return res.data as T_Api_Success_Res<ApplicationSchemaDto>;
      },
      enabled: !!applicationId,
    });
  },

  useCreateApplication: () => {
    // const orgMeta = useAppSelector((state) => state.orgMeta);
    return useMutation({
      mutationKey: ["application/create"],
      mutationFn: async (body: { referrer_code: string | undefined; applicationType: BankFetchMethod }) => {
        const { applicationType, ...rest } = body;
        const res = await getClearApi.post(`/application?type=${applicationType}`, { ...rest });
        return res.data as T_Api_Success_Res<ApplicationSchemaDto>;
      },
    });
  },

  useUpdateApplication: (applicationId: string | null) => {
    return useMutation({
      mutationKey: ["application/update", applicationId],
      mutationFn: async (body: {
        client_details: ApplicationSchemaDto["client_details"];
        business_info: ApplicationSchemaDto["business_info"];
        selected_bank: ApplicationSchemaDto["selected_bank"];
      }) => {
        const res = await getClearApi.put(`/application/${applicationId}/update`, body);
        return res.data as T_Api_Success_Res<ApplicationSchemaDto>;
      },
    });
  },

  useDeleteApplication: () => {
    return useMutation({
      mutationKey: ["application/delete"],
      mutationFn: async (body: { application_id: string }) => {
        const res = await getClearApi.post(`/application/delete`, body);
        return res.data as T_Api_Success_Res<{ _id: string; upstream_application_id: string }>;
      },
    });
  },

  useUpdateStatus: (applicationId: string | null) => {
    return useMutation({
      mutationKey: ["application", "status", applicationId],
      mutationFn: async (body: {
        status: keyof Pick<ApplicationSchemaDto, "status">["status"];
        summary?: Partial<ApplicationSchemaDto["summary"]>;
      }) => {
        const res = await getClearApi.patch(`/application/${applicationId}/status`, body);
        return res.data as T_Api_Success_Res<ApplicationSchemaDto>;
      },
    });
  },

  useGetArtifacts: <T extends "loans" | "agreements" | "banks" | "lawsuits">(
    applicationId: string | null,
    artifact: T,
  ) => {
    type _T_ArtifactMap = {
      loans: LoanSchemaDto;
      agreements: { attachments: string[] };
      banks: BankSchemaDto;
      lawsuits: LawSuitSchemaDto;
    };
    return useQuery({
      queryKey: ["application", "artifact/all", artifact, applicationId],
      queryFn: async () => {
        const res = await api.get(`/application/${applicationId}/${artifact}`);
        return res.data as T_Api_Success_Res<T_Populate<_T_ArtifactMap[T], { attachments: FileSchemaDto[] }>[]>;
      },
      enabled: !!applicationId,
    });
  },

  // useAddLoanStatement: () => {
  //   return useMutation({
  //     mutationKey: ["loan/add-statement"],
  //     mutationFn: (body: LoanDto) => {
  //       return api.post("/application/create/loan", body);
  //     },
  //   });
  // },

  useCommitArtifact: <T extends "loans" | "agreements" | "banks">(artifact: T) => {
    type T_Response = {
      loans: {
        refreshEveryMilis: number;
        refreshInMilis: number;
        reloadInMilis: number;
        summary: {
          processed: number;
          received: number;
          existing: number;
          loans: string[];
        };
      };
      banks: { refreshInMilis: number };
      agreements: any;
    };
    return useMutation({
      mutationKey: ["application/commit", artifact],
      mutationFn: async ({
        applicationId,
        body,
      }: {
        applicationId: string;
        body: {
          reset?: boolean;
          attachments: string[];
        };
      }) => {
        const res = await api.put(`application/${applicationId}/${artifact}`, body);
        return res.data as T_Api_Success_Res<T_Response[T]>;
      },
    });
  },
  useCommitLawsuit: () => {
    return useMutation({
      mutationKey: ["application/commit", "lawsuits"],
      mutationFn: async ({
        applicationId,
        body,
      }: {
        applicationId: string;
        body: {
          reset?: boolean;
          lawsuits: {
            loan_id: string;
            loan_name?: string;
            status?: LawSuitTypes;
            attachments: string[];
          }[];
        };
      }) => {
        const res = await api.put(`application/${applicationId}/lawsuits`, body);
        return res.data as T_Api_Success_Res<{ new_lawsuits: LawSuitSchemaDto[] }>;
      },
    });
  },

  useGetStrategy: (
    applicationId: string | null | undefined,
    extraOptions?: { weekly?: number; total_weeks?: number; debt_override?: number },
    // customPlanAmount: number | undefined,
  ) => {
    // const modifiedExtraOptions: typeof extraOptions = extraOptions;
    // if (Object.keys(extraOptions).length === 0 && typeof customPlanAmount === "number") {
    //   modifiedExtraOptions.weekly = customPlanAmount;
    // }
    return useQuery({
      queryKey: ["application/strategy", applicationId, extraOptions],
      queryFn: async () => {
        const res = await api.get(`/application/${applicationId}/strategy`, {
          params: extraOptions,
        });
        return res.data as T_Api_Success_Res<{
          business_name: string;
          address: string;
          strategies: Strategy[];
          total_debt: number;
          active_loans: number;
          active_lawsuits: number;
          monthly_revenue: number;
          profit_margin: number;
          hardship_score: string;
          hardship_text: string;
        }>;
      },
      enabled: !!applicationId,
    });
  },
  useChooseStrategy: () => {
    return useMutation({
      mutationKey: ["application/stategy", "choose"],
      mutationFn: async ({
        applicationId,
        ...body
      }: {
        applicationId: string;
        strategy: number;
        weekly_budget?: number;
      }) => {
        const res = await api.post(`/application/${applicationId}/strategy`, body);
        return res.data;
      },
    });
  },
  useStartSignDocPrep: () => {
    return useMutation({
      mutationKey: ["application", "pandadoc"],
      mutationFn: async ({ applicationId }: { applicationId: string }) => {
        const res = await api.post(`/application/${applicationId}/pandadoc`, {});
        return res.data;
      },
    });
  },
  useGetPandaDocLink: (applicationId: string | null) => {
    return useQuery({
      queryKey: ["application/pandadoc", applicationId],
      queryFn: async () => {
        const res = await api.get(`/application/${applicationId}/pandadoc`);
        return res.data as T_Api_Success_Res<{
          pandadoc: (NonNullable<ApplicationSchemaDto["external_links"]["pandadoc"]>[number] & { link: string })[];
        }>;
      },
      enabled: !!applicationId,
      retryDelay: 5 * 1000,
      retry: (retryCount: number, error) => {
        if (error.response?.status === 404 && retryCount <= 6) return true;
        else return false;
      },
    });
  },
  useGetGraphs: (applicationId: string | null) => {
    return useQuery({
      queryKey: ["application/graphs", applicationId],
      queryFn: async () => {
        const res = await api.get(`/application/graphs/${applicationId}`);
        return res.data as T_Api_Success_Res<{
          cash_flow?: { id: string; data: { x: string; y: string }[] }[];
          debtsmart_returns?: { id: string; debtsmart_return: number }[];
          optimisations?: {
            effective_apr: number;
            effective_total_due: number;
            total_weeks: number;
            weekly_payment: number;
          };
          original_vs_optimal_payment?: { id: string; optimal_amount: number; original_amount: number }[];
          revenue_expense?: { id: string; data: { x: string; y: string }[] }[];
          stated_APR_data?: { due_amount: number; stated_APR: number }[];
          external?: {
            charts: {
              fundedAmountByCreditor: { creditor: string; fundedAmount: number }[];
              paybackAmountByCreditor: { creditor: string; paybackAmount: number }[];
              paymentAmountByCreditor: { creditor: string; paymentAmount: number }[];
              fundedVsPaybackAmount: { creditor: string; fundedAmount: number; paybackAmount: number }[];
              paybackPercentage: { creditor: string; paybackPercentage: number }[];
              paymentFrequencyDistribution: { paymentFrequency: string; count: number }[];
              totalAmountsAcrossAllCreditors: { type: string; totalAmount: number }[];
              averagePaymentAmountByPaymentFrequency: { paymentFrequency: string; averagePaymentAmount: number }[];
              creditorsPaymentAsPercentageOfFunded: { creditor: string; paymentPercentage: number }[];
            };
          };
        }>;
      },
      enabled: !!applicationId,
      retryDelay: 2 * 1000,
      retry: (retryCount: number, error) => {
        if (error.response?.status === 400 && retryCount < 5) return true;
        else return false;
      },
      gcTime: 0,
    });
  },
  useUpdateManualLawsuit: () => {
    return useMutation({
      mutationKey: ["application/commit", "lawsuits/manual"],
      mutationFn: async ({
        applicationId,
        body,
      }: {
        applicationId: string;
        body: {
          lawsuit_insurance?: boolean;
          lawsuits?: {
            loan_id: string;
            loan_name?: string;
            status?: LawSuitTypes;
            attachments?: string[];
          }[];
        };
      }) => {
        const res = await api.put(`/application/${applicationId}/lawsuits/manual`, body);
        return res.data as T_Api_Success_Res<{ new_lawsuits: LawSuitSchemaDto[] }>;
      },
    });
  },
  useGetPaymentLink: (applicationId: string | null) => {
    return useQuery({
      queryKey: ["application", "get/payment-link"],
      queryFn: async () => {
        const res = await api.get(`/application/${applicationId}/payment-link`);
        return res.data as T_Api_Success_Res<{ link: string }>;
      },
      enabled: !!applicationId,
    });
  },
  useStorePaymentToken: () => {
    return useMutation({
      mutationKey: ["application/payment-token"],
      mutationFn: async ({
        applicationId,
        body,
      }: {
        applicationId: string;
        body: {
          payment_type: "actum" | "seamless" | "easypay";
          payment_token: string;
        };
      }) => {
        const res = await api.post(`/application/payment-token/${applicationId}`, body);
        // return res.data as T_Api_Success_Res<{}>;
      },
    });
  },
  useRequestManagerApproval: () => {
    return useMutation({
      mutationKey: ["application/manager-approval"],
      mutationFn: async (body: { application_id: string; budget: number }) => {
        const res = await api.post(`/application/manager-approval`, body);
        // return res.data as T_Api_Success_Res<{}>;
      },
    });
  },
  useResetModule: () => {
    return useMutation({
      mutationKey: ["application/reset"],
      mutationFn: async ({
        application_id,
        body,
      }: {
        application_id: string;
        body: { module: (typeof APPLICATION_STATUS)["LAWSUITS"] };
      }) => {
        const res = await api.post(`/application/reset/${application_id}`, body);
        // return res.data as T_Api_Success_Res<{}>;
      },
    });
  },
  useGetDebtorSummary: (applicationId: string | null) => {
    return useQuery({
      queryKey: ["application", "debtor-summary", applicationId],
      queryFn: async () => {
        const res = await api.get(`/application/${applicationId}/debtor-summary`);
        return res.data as T_Api_Success_Res<{
          debtor_summary?: IntelligenceDto;
          categorised_credit_transactions: Record<
            string,
            Record<string, { amount: number; count: number; percentage: number }>
          >;
          debtsmart_returns?: { id: string; debtsmart_return: number }[];
          total_categorised_credit_transactions: Record<string, { amount: number; count: number; percentage: number }>;
          original_vs_optimal?: { id: string; optimal_amount: number; original_amount: number }[];
          month_wise_profit: Record<
            string,
            {
              credit: number;
              debit: number;
              profit: number;
              mca: number;
              profit_without_mca: number;
              profit_with_mca: number;
            }
          >;
          weekly_credits: {
            totals: Record<string, { count: number; amount: number; percentage: number }>;
            weekly_credits: Record<string, Record<string, { count: number; amount: number; percentage: number }>>;
          };
          creditor_monthly_withhold?: CreditorMonthlyWithhold[];
          profit_margin: number | null;
          categorised_credit_transaction_list:
            | {
                date: string;
                description: string;
                amount: string;
                category: string;
                year_month: string;
              }[]
            | undefined;
          withdrawal_percentage: Record<string, { mca_total: number; percent: number; revenue: number }>;
          balance_comparison: {
            date: string;
            balance: number;
            true_balance: number;
          }[];
          negative_days: { year_month: string; count: number }[];
          nsf_transactions: { year_month: string; count: number }[];
        }>;
      },
      enabled: !!applicationId,
    });
  },
  useCreateExternalLink: () => {
    return useMutation({
      mutationKey: ["application", "create-external-link"],
      mutationFn: async ({ email }: { email: string }) => {
        let url = `/application/create-external-link`;
        if (email) {
          url = `/application/create-external-link?email=${email}`;
        }
        const res = await api.post(url);
        return res.data as T_Api_Success_Res<{ key: string; scopes: string; url: string }>;
      },
    });
  },

  useShareApplication: () => {
    return useMutation({
      mutationKey: ["application", "share-application"],
      mutationFn: async ({ email, appkey }: { email: string; appkey: string }) => {
        await api.post(`/application/share`, {
          appkey,
          email,
        });
      },
    });
  },
};

export type Strategy = {
  key: string;
  title: string;
  sub_title: string;
  features: string[];
  main_payment_title: string;
  main_payment_amount: number;
  sub_payment_title: string;
  sub_payment_amount: number;
  footer_text?: string;
  highlighted?: boolean;
  total_weeks: number;
  program_tenure?: number;
  common: {
    lawsuit_initial: number;
    lawsuit_weekly: number;
    service_fee_weekly: number;
    interest: number;
    interest_rate?: number;
    initial_payment: number;
    weekly_payments: number;
    total_payable: number;
    strategy_clause: number;
    is_custom_divided: boolean;
    is_managers_approval: boolean;
    selector_idx: number;
    is_initial_split?: boolean;
    split_at?: number;
  };
};
